/* PaintLab - offer-aware trade price block.
 *
 * assets/dollarlabs-b2b.js renders one label for every card ("Your trade price")
 * and compares the trade price against the variant's CURRENT price. On a
 * clearance or sale product that current price is already marked down, so the
 * card understates the saving (RP 7.80 - save 1.17, when the product is 8.99
 * normally and the real saving is 2.36) and gives no sign the item is on offer.
 * The app also hides the green discount chip for trade customers, so without
 * this there is nothing at all in the price block to say "this is on offer".
 *
 * This corrects both, off the compare-at prices the theme emits on the card as
 * data-pl-compare, and off the Clearance / Sale flag added in
 * snippets/product-grid-item.liquid.
 *
 * Runs from a MutationObserver, whose callback is a microtask - it lands in the
 * same frame as the app's own write, so the uncorrected figures are never painted.
 *
 * FAIL-SAFE: anything that does not parse cleanly is left exactly as the app
 * rendered it. Deleting this file returns the cards to stock app behaviour.
 */
(function () {
  'use strict';

  var LABELS = {
    clearance: 'Clearance price',
    sale: 'Sale price'
  };

  var cfgCache;
  function cfg() {
    if (cfgCache === undefined) {
      var el = document.getElementById('dl-b2b-config');
      try { cfgCache = el ? JSON.parse(el.textContent) : null; } catch (e) { cfgCache = null; }
    }
    return cfgCache;
  }

  function moneyToken() {
    var fmt = (cfg() && cfg().moneyFormat) || '';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    return m ? m[1] : 'amount';
  }
  function commaDecimal() { return moneyToken().indexOf('comma_separator') > -1; }

  function withDelimiters(cents, precision, thousands, decimal) {
    var parts = (cents / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  /* Mirrors the money() in assets/dollarlabs-b2b.js so both blocks format alike. */
  function money(cents) {
    var fmt = cfg() && cfg().moneyFormat;
    if (!fmt) return (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount': return withDelimiters(cents, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ', '.');
        case 'amount_with_space_separator': return withDelimiters(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(cents, 2, ' ', '.');
        default: return match;
      }
    });
  }

  function parseMoney(text) {
    if (!text) return null;
    var m = String(text).match(/\d[\d.,\s']*/);
    if (!m) return null;
    var raw = m[0].replace(/[\s']/g, '');
    raw = commaDecimal() ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, '');
    var n = parseFloat(raw);
    return isNaN(n) ? null : Math.round(n * 100);
  }

  function esc(text) {
    return String(text == null ? '' : text).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }

  function kindOf(card) {
    if (card.querySelector('.pl-flag--clearance')) return 'clearance';
    if (card.querySelector('.pl-flag--sale')) return 'sale';
    return null;
  }

  /* "780:899,1750:2200" -> { 780: 899, 1750: 2200 } */
  function compareMap(card) {
    var box = card.querySelector('[data-pl-compare]');
    var raw = box && box.getAttribute('data-pl-compare');
    if (!raw) return null;
    var map = {}, found = false;
    raw.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var price = parseInt(parts[0], 10), compare = parseInt(parts[1], 10);
      if (price > 0 && compare > price && !(price in map)) { map[price] = compare; found = true; }
    });
    return found ? map : null;
  }

  function fixPrice(card, kind) {
    var block = card.querySelector('.dl-price');
    if (!block || block.getAttribute('data-pl-done') === '1') return;
    block.setAttribute('data-pl-done', '1');

    var eyebrow = block.querySelector('.dl-price__eyebrow');
    if (eyebrow) {
      eyebrow.textContent = LABELS[kind];
      /* Lets the stylesheet tighten the longer wording onto one line. */
      block.classList.add('pl-offer-price');
    }

    var meta = block.querySelector('.dl-price__meta');
    var rrp = block.querySelector('.dl-price__rrp');
    var now = block.querySelector('.dl-price__now');
    if (!meta || !rrp || !now) return;

    var map = compareMap(card);
    if (!map) return;

    var base = parseMoney(rrp.textContent);
    var paid = parseMoney(now.textContent);
    if (base === null || paid === null) return;

    var compare = map[base];
    if (!compare || compare <= base || paid >= compare) return;

    var member = (cfg() && cfg().member) || {};
    meta.innerHTML = esc(member.rrpLabel || 'RRP') +
      ' <s class="dl-price__rrp">' + money(compare) + '</s> &middot; ' +
      esc(member.saveLabel || 'save') + ' ' + money(compare - paid);
  }

  /* assets/dollarlabs-b2b.js's cardSaleMarkup() emits only the two incl-VAT
     spans - it drops the excl-VAT line the theme renders. So a card the app
     re-prices ends up one row shorter than its neighbours and the prices in a
     row stop lining up. Put the missing row back, using the app's own formula
     (exVat = round(cents / (1 + vatRate))) and the wording the theme uses.
     Idempotent: skips a block that already has the row, and a fresh re-render
     arrives without it, so it is simply added again. */
  function ensureExVat(box) {
    var wrap = box.querySelector('.dual-pricing');
    if (!wrap) return;
    var incl = wrap.querySelector('.vat__product:not(.compare-price)');
    if (!incl || wrap.querySelector('.ex__product:not(.compare-price)')) return;
    var cents = parseMoney(incl.textContent);
    if (cents === null) return;
    var rate = (cfg() && cfg().vatRate) || 0.23;
    var span = document.createElement('span');
    span.className = 'ex__product';
    span.textContent = money(Math.round(cents / (1 + rate))) + ' excl. VAT';
    wrap.appendChild(span);
  }

  /* Same omission again, on the OTHER branch. cardMemberMarkup() - the block a
     logged-in Trade customer sees - has no excl-VAT line either, so trade buyers
     lost the one figure they think in while logged-out visitors kept it.
     Sits directly under the incl-VAT price, mirroring the retail card. */
  function ensureTradeExVat(box) {
    var block = box.querySelector('.dl-price');
    if (!block) return;
    var now = block.querySelector('.dl-price__now');
    if (!now || block.querySelector('.dl-price__excl')) return;
    var cents = parseMoney(now.textContent);
    if (cents === null) return;
    var rate = (cfg() && cfg().vatRate) || 0.23;
    var span = document.createElement('span');
    span.className = 'dl-price__excl';
    span.textContent = money(Math.round(cents / (1 + rate))) + ' excl. VAT';
    now.parentNode.insertBefore(span, now.nextSibling);
  }

  /* Put the discount percentage on the Clearance / Sale flag.
     It cannot come from Liquid: most of these products carry no Shopify
     compare-at price at all - the reduction is applied by the pricing app at
     runtime - so the only place both numbers exist together is the rendered
     card. Reads the trade block first, then the retail spans, then the
     compare-at map the theme emits. Appends a span rather than rewriting the
     flag's text, so the word survives if no percentage can be worked out. */
  /* The discount shown on the flag must describe the PRODUCT, not whichever
     variant the card happens to render.
     Juno J-25 is the case that exposed this: the card shows the 4L, which for a
     retail visitor is not reduced at all, while the 15L is 27% off. Reading the
     rendered price gave "SALE -15%" on a product whose real offer is nearly
     double that. Osmo Polyx has the same shape.
     So ask the pricing app about EVERY available variant, combine that with any
     Shopify compare-at the theme emitted, and take the best. "up to" whenever the
     variants differ or some are not reduced - otherwise the figure would overstate
     the offer on the variants that are not on sale. */
  var pctCache = {};

  function variantPcts(card) {
    var box = card.querySelector('[data-dl-card-price]');
    var raw = box && box.getAttribute('data-dl-variants');
    if (!raw) return Promise.resolve(null);
    if (pctCache[raw]) return pctCache[raw];

    var map = compareMap(card) || {};
    var fn = window.DollarlabsGetBestPrice;
    var pairs = [];
    raw.split(',').forEach(function (p) {
      var a = p.split(':'), id = a[0], base = parseInt(a[1], 10);
      if (id && base > 0) pairs.push({ id: id, base: base });
    });
    if (!pairs.length) return Promise.resolve(null);

    var job = Promise.all(pairs.map(function (v) {
      var rrp = map[v.base] && map[v.base] > v.base ? map[v.base] : v.base;
      if (typeof fn !== 'function') {
        return Promise.resolve(rrp > v.base ? Math.round((rrp - v.base) / rrp * 100) : null);
      }
      return Promise.resolve(fn(v.id, 1)).then(function (r) {
        var now = (r && r.hasDiscount) ? r.priceInCents : v.base;
        var was = Math.max(rrp, (r && r.originalPrice) || 0, v.base);
        return was > now ? Math.round((was - now) / was * 100) : null;
      }).catch(function () {
        return rrp > v.base ? Math.round((rrp - v.base) / rrp * 100) : null;
      });
    })).then(function (list) {
      var real = list.filter(function (n) { return n !== null && n > 0; });
      if (!real.length) return null;
      var max = Math.max.apply(null, real);
      var min = Math.min.apply(null, real);
      return { max: max, varies: max !== min || real.length < list.length };
    });

    /* Only cache once the pricing app is actually present. Before it loads,
       the compare-at fallback is all we have, and for a product with no Shopify
       compare-at that is null - caching that would mean the percentage never
       appears, which is exactly what happened on the Sale page. */
    if (typeof fn === 'function') pctCache[raw] = job;
    return job;
  }

  function badgePct(card) {
    var flag = card.querySelector('.pl-flag');
    if (!flag) return;
    variantPcts(card).then(function (r) {
      var tag = flag.querySelector('.pl-flag__pct');
      if (!r) { if (tag) tag.remove(); return; }
      var text = (r.varies ? 'up to \u2212' : '\u2212') + r.max + '%';
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'pl-flag__pct';
        flag.appendChild(tag);
      }
      if (tag.textContent !== text) tag.textContent = text;
      hideDiscountChip(card);
    });
  }

  /* The green -13% chip in the opposite corner now says the same thing as the
     flag, so retire it once the flag carries the figure. Only the discount
     chip - the Sold Out chip shares the same container and must survive. */
  function hideDiscountChip(card) {
    var chip = card.querySelector('.product-label .sale-label');
    if (chip) chip.style.display = 'none';
    var label = card.querySelector('.product-label');
    if (label && !label.querySelector('.sold-out-label')) label.style.display = 'none';
  }

  /* The app tags every card it prices as .on-sale and hides the top-right
     discount chip for trade customers. Without this the flag would drop to a
     second row on mobile with nothing beside it. Measure the rendered box, not
     computed display - a child of a display:none parent still computes visible. */
  function markChip(card) {
    var label = card.querySelector('.product-label');
    var visible = false;
    if (label) {
      var r = label.getBoundingClientRect();
      visible = r.width > 0 && r.height > 0;
    }
    card.classList.toggle('pl-nochip', !visible);
  }

  /* Keep every trade price on one line, at ONE size across the whole grid.
     The trade block is narrower than the retail one - the green bar and its
     padding take 15px of the 180px column - so a four-figure price wrapped and
     that card grew taller than its neighbours. Sizes cannot be hard-coded: what
     fits depends on the longest price actually on the page.
     So: measure what each row needs, take the single worst case, and apply that
     one scale to every block. All cards stay identical, and nothing wraps
     whatever the prices happen to be. Rows are nowrap in CSS, so scrollWidth
     reports the true single-line width. Sizes are cleared before measuring, so
     repeated runs cannot compound. */
  var BASE = { now: 20, meta: 13, excl: 13 };

  function fitTradeBlocks() {
    var blocks = [], all = document.querySelectorAll('.dl-price'), i;
    for (i = 0; i < all.length; i++) blocks.push(all[i]);
    if (!blocks.length) return;

    var scale = 1;
    blocks.forEach(function (b) {
      var cs = getComputedStyle(b);
      var inner = b.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      if (!(inner > 0)) return;
      Object.keys(BASE).forEach(function (k) {
        var e = b.querySelector('.dl-price__' + k);
        if (!e) return;
        e.style.fontSize = '';
        var need = e.scrollWidth;
        if (need > inner) scale = Math.min(scale, inner / need);
      });
    });

    if (scale > 0.995) return;
    scale = Math.max(scale, 0.7);
    blocks.forEach(function (b) {
      Object.keys(BASE).forEach(function (k) {
        var e = b.querySelector('.dl-price__' + k);
        if (e) e.style.fontSize = (BASE[k] * scale).toFixed(2) + 'px';
      });
    });
  }

  function run() {
    var boxes = document.querySelectorAll('[data-dl-card-price]');
    for (var b = 0; b < boxes.length; b++) { ensureExVat(boxes[b]); ensureTradeExVat(boxes[b]); }
    var flags = document.querySelectorAll('.pl-flag');
    for (var i = 0; i < flags.length; i++) {
      var card = flags[i].closest('.product-item');
      if (!card) continue;
      var kind = kindOf(card);
      if (!kind) continue;
      markChip(card);
      fixPrice(card, kind);
      badgePct(card);
    }
    fitTradeBlocks();
  }

  function debounce(fn, ms) {
    var timer;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
  }

  function start() {
    run();
    var rescan = debounce(run, 60);
    new MutationObserver(rescan).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', rescan);
    [200, 800, 2000, 3500, 5000, 7000, 9000].forEach(function (ms) { setTimeout(run, ms); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
